import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';
import { FiCheckCircle, FiClock, FiDollarSign, FiUsers, FiCalendar, FiAward } from 'react-icons/fi';

const Home = () => {
  const { isAuthenticated } = useAuth();
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchSettings();
    fetchCategories();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories?isActive=true');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  // Use settings if loaded, otherwise use defaults
  const features = settings?.features || [
    {
      icon: <FiUsers className="text-4xl text-primary-500" />,
      title: 'Expert Tutors',
      description: 'Learn from experienced math instructors with proven teaching methods'
    },
    {
      icon: <FiCalendar className="text-4xl text-primary-500" />,
      title: 'Flexible Scheduling',
      description: 'Book classes at times that work best for your schedule'
    },
    {
      icon: <FiClock className="text-4xl text-primary-500" />,
      title: 'One-on-One Sessions',
      description: 'Get personalized attention with private tutoring sessions'
    },
    {
      icon: <FiDollarSign className="text-4xl text-primary-500" />,
      title: 'Affordable Pricing',
      description: 'Quality education at competitive rates with secure payment'
    },
    {
      icon: <FiCheckCircle className="text-4xl text-primary-500" />,
      title: 'Email Reminders',
      description: 'Never miss a class with automated email notifications'
    },
    {
      icon: <FiAward className="text-4xl text-primary-500" />,
      title: 'All Levels Welcome',
      description: 'From beginner to advanced, we have courses for everyone'
    }
  ];

  const statistics = settings?.statistics || {
    students: { value: '500+', label: 'Students Taught' },
    success: { value: '95%', label: 'Success Rate' },
    rating: { value: '4.9/5', label: 'Rating' }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="fade-in">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                {settings?.heroTitle || 'Master Math with Expert Online Tutoring'}
              </h1>
              <p className="text-xl mb-8 text-primary-100">
                {settings?.heroSubtitle || 'Book personalized one-on-one math sessions with experienced instructors. Learn at your own pace, on your own schedule.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                {isAuthenticated ? (
                  <Link
                    to="/booking"
                    className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-2xl transition transform hover:-translate-y-1 text-center"
                  >
                    Book a Session
                  </Link>
                ) : (
                  <Link
                    to="/register"
                    className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-2xl transition transform hover:-translate-y-1 text-center"
                  >
                    Get Started
                  </Link>
                )}
                <Link
                  to="/courses"
                  className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-primary-600 transition text-center"
                >
                  View Courses
                </Link>
              </div>
              <div className="mt-8 flex items-center space-x-8">
                <div>
                  <div className="text-3xl font-bold">{statistics.students.value}</div>
                  <div className="text-primary-100">{statistics.students.label}</div>
                </div>
                <div>
                  <div className="text-3xl font-bold">{statistics.success.value}</div>
                  <div className="text-primary-100">{statistics.success.label}</div>
                </div>
                <div>
                  <div className="text-3xl font-bold">{statistics.rating.value}</div>
                  <div className="text-primary-100">{statistics.rating.label}</div>
                </div>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
                <img
                  src="https://images.unsplash.com/photo-1509228468518-180dd4864904?w=600&h=400&fit=crop"
                  alt="Math tutoring"
                  className="rounded-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{settings?.aboutTitle || 'Why Choose Us?'}</h2>
            <p className="text-xl text-gray-600">{settings?.aboutDescription || 'Everything you need for successful math learning'}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition card-hover"
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subjects Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Subjects We Cover</h2>
            <p className="text-xl text-gray-600">Comprehensive math tutoring across all levels</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((category) => (
              <div
                key={category.id}
                className="p-6 rounded-xl text-center hover:shadow-lg transition card-hover"
                style={{ 
                  background: `linear-gradient(135deg, ${category.color}15, ${category.color}30)`,
                }}
              >
                <div className="text-3xl mb-2">{category.icon || '📚'}</div>
                <span className="text-lg font-semibold" style={{ color: category.color }}>
                  {category.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">Get started in just 3 simple steps</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary-500 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">
                1
              </div>
              <h3 className="text-2xl font-semibold mb-4">Sign Up</h3>
              <p className="text-gray-600">
                Create your account with email verification to get started
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-primary-500 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">
                2
              </div>
              <h3 className="text-2xl font-semibold mb-4">Choose & Book</h3>
              <p className="text-gray-600">
                Select a course and book your preferred time slot from our calendar
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-primary-500 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">
                3
              </div>
              <h3 className="text-2xl font-semibold mb-4">Start Learning</h3>
              <p className="text-gray-600">
                Pay securely and receive your Zoom link before the class starts
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-500 to-secondary-500 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Excel in Math?</h2>
          <p className="text-xl mb-8">
            Join hundreds of students who have improved their math skills with our expert tutoring
          </p>
          {isAuthenticated ? (
            <Link
              to="/booking"
              className="inline-block bg-white text-primary-600 px-10 py-4 rounded-lg font-semibold text-lg hover:shadow-2xl transition transform hover:-translate-y-1"
            >
              Book Your First Session
            </Link>
          ) : (
            <Link
              to="/register"
              className="inline-block bg-white text-primary-600 px-10 py-4 rounded-lg font-semibold text-lg hover:shadow-2xl transition transform hover:-translate-y-1"
            >
              Sign Up Now - It's Free!
            </Link>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;

